/**
 * 宮城県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は福岡・北海道・山形と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - ずんだの語源は断定しない（甚太説・陣太刀説・「豆ん打」説を農水省が併記）。
 *  - 「伊達政宗がはらこ飯を考案した」は誤り。献上されたという伝承まで。
 *  - 「伊達政宗が笹かまぼこを命名した」は誤り。家紋の笹にちなみ昭和に統一、まで。
 *  - 「仙台は牛たん発祥の地」と一般化しない。佐野啓四郎が考案した、まで。
 *  - 金華さばを「金華山沖で獲れたサバ」と書かない。定義は石巻魚市場に水揚げされたもの。
 *  - 仙台牛は「肉質等級5等級だけ」。A5・B5という書き方は県公式では裏が取れない。
 *  - 店名（定義とうふ店・太助など）は表示名に使わない。
 *
 * 本番画像は public/assets/foods/miyagi/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：ずんだ（枝豆）（店で食べられる品に差し替え）
 * - 松島の焼きがき：かき小屋の営業期間・食べ放題の時間や値段：変わる情報なので書かない
 * - 松島の焼きがき：「日本三景」：観光協会サイトの名称にあるが、料理の説明に要らないので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { MIYAGI_FOOD_HULLS } from './miyagiFoodShapes.generated';
import { miyagiSceneryTour } from './miyagiScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'sasa-kamaboko',
    level: 1,
    name: '笹かまぼこ',
    shortName: '笹かま',
    image: '/assets/foods/miyagi/01_sasa_kamaboko.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '笹の葉の形をした焼きかまぼこ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/sasa_kamaboko_no_isobeage_miyagi.html',
    icon: { label: '笹かま', shape: 'mochi', baseColor: '#efe3c8', accentColor: '#c9a45a' },
    reward: {
      unlockScore: 200,
      area: '宮城県',
      headline: '明治の中ごろ、ヒラメが大量に獲れたときの保存法として生まれた焼きかまぼこ。',
      tip: 'はじめは「手のひらかまぼこ」「ベロかまぼこ」と呼ばれていた。伊達家の家紋「竹に雀」の笹にちなんで呼ばれるようになり、昭和に入って名前が統一された。いまは県内40軒以上が作っている。',
      links: searchLinks('宮城県', '笹かまぼこ'),
    },
  },
  {
    id: 'sankaku-aburaage',
    level: 2,
    name: '三角あぶらあげ',
    shortName: '三角揚げ',
    image: '/assets/foods/miyagi/02_sankaku_aburaage.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'ぶ厚い三角の、揚げたて。',
    source: 'https://www.sentabi.jp/spots/444',
    icon: { label: '油揚', shape: 'mochi', baseColor: '#d9a256', accentColor: '#8c5a24' },
    reward: {
      unlockScore: 500,
      area: '仙台・定義',
      headline: '定義如来への精進料理の豆腐を作るため、明治23年に開いた豆腐店の名物。',
      tip: '三角なのは、丸い揚げ鍋で一度にたくさん揚げるための工夫。揚げたてに七味と醤油をかけて食べる。',
      links: searchLinks('仙台市', '定義 三角あぶらあげ'),
    },
  },
  {
    id: 'zunda-mochi',
    level: 3,
    name: 'ずんだ餅',
    shortName: 'ずんだ餅',
    image: '/assets/foods/miyagi/03_zunda_mochi.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'つきたての餅に、鮮やかな緑の餡。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/zunda_mochi_miyagi.html',
    icon: { label: 'ずんだ', shape: 'mochi', baseColor: '#9cc45a', accentColor: '#e8e0c8' },
    reward: {
      unlockScore: 1000,
      area: '仙台',
      headline: '正月も婚礼も法事も餅。宮城の餅文化から育った代表格。',
      tip: 'ずんだ餡の味つけは砂糖と塩。あの緑は枝豆そのままの色。',
      links: searchLinks('仙台市', 'ずんだ餅'),
    },
  },
  {
    id: 'shiroishi-umen',
    level: 4,
    name: '白石温麺',
    shortName: '温麺',
    image: '/assets/foods/miyagi/04_shiroishi_umen.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '油を使わない、短いそうめん。',
    source: 'https://www.maff.go.jp/j/keikaku/syokubunka/traditional-foods/menu/siroisiuumen.html',
    icon: { label: '温麺', shape: 'noodle-bowl', baseColor: '#f0e6cf', accentColor: '#8fa9c4' },
    reward: {
      unlockScore: 1800,
      area: '白石',
      headline: '400年以上前から白石市のあたりで作られている。素麺の一種だが、製造に油を使わない。',
      tip: '長さは9センチと短く、太さは0.3ミリほどと素麺より少し太い。病気の父に食べさせるために作った、という話が伝わっている。',
      links: searchLinks('白石市', '白石温麺'),
    },
  },
  {
    id: 'matsushima-yakigaki',
    level: 5,
    name: '松島の焼きがき',
    shortName: '松島の焼きがき',
    image: '/assets/foods/miyagi/05_matsushima_yakigaki.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '小粒ながら味が凝縮された松島産のかき。水揚げしたばかりの新鮮なかきを、生でもフライでも、鍋や天ぷらでも味わう。町では10月から3月にかけて売られる。',
    source:
      'https://www.town.miyagi-matsushima.lg.jp/page/1128.html',
    icon: { label: '焼きがき', shape: 'sashimi-platter', baseColor: '#8a8a84', accentColor: '#f0e2c0' },
    reward: {
      unlockScore: 3000,
      area: '松島町',
      headline: '小粒で味の濃い松島のかき',
      tip: '町の中には海産物を焼く店が多く、いい香りが漂う。松島観光協会のかき小屋や、焼きがきを食べられる店が並ぶ。',
      links: searchLinks('松島町', '焼きがき'),
      official: [{ label: '松島観光協会「食べる」', url: 'https://www.matsushima-kanko.com/taberu/' }, { label: '松島観光協会かき小屋', url: 'https://www.matsushima-kanko.com/taberu/detail.php?id=287' }],
    },
  },
  {
    id: 'aburafu',
    level: 6,
    name: '油麩（仙台麩）',
    shortName: '油麩',
    image: '/assets/foods/miyagi/06_aburafu.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: 'フランスパンのような形をした、棒状の揚げ麩。',
    source: 'https://www.maff.go.jp/j/keikaku/syokubunka/traditional-foods/menu/aburahu.html',
    icon: { label: '油麩', shape: 'mochi', baseColor: '#c58b44', accentColor: '#f0dcb4' },
    reward: {
      unlockScore: 4500,
      area: '登米',
      headline: '長さ25センチほどの棒状。小麦のグルテンを油で揚げた、全国でも珍しい揚げ麩。',
      tip: '明治の末に登米の豆腐店が考え出した。冷蔵庫のない時代、盆の精進料理の油揚げや豆腐が傷んでしまう、という困りごとへの答えだった。',
      links: searchLinks('登米市', '油麩丼'),
    },
  },
  {
    id: 'harako-meshi',
    level: 7,
    name: 'はらこ飯',
    shortName: 'はらこ飯',
    image: '/assets/foods/miyagi/07_harako_meshi.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '鮭の煮汁で炊いた飯に、身といくら。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/harako_meshi_miyagi.html',
    icon: { label: 'はらこ', shape: 'rice-bowl', baseColor: '#e07a4c', accentColor: '#f3e2c0' },
    reward: {
      unlockScore: 6500,
      area: '亘理',
      headline: '阿武隈川をのぼる鮭を獲る漁師の食事が始まりとされる。「はらこ」は鮭の腹の子のこと。',
      tip: '伊達政宗が荒浜の運河工事を見に来たとき、領民から献上されたと伝わる。昔は混ぜご飯だった。',
      links: searchLinks('亘理町', 'はらこ飯'),
    },
  },
  {
    id: 'kinka-saba',
    level: 8,
    name: '金華さば',
    shortName: '金華さば',
    image: '/assets/foods/miyagi/08_kinka_saba.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '脂ののった、大型のマサバ。',
    source: 'https://isiuo.co.jp/kinka_brand/kinka_mackerel/',
    icon: { label: '金華', shape: 'fish', baseColor: '#5b7f9c', accentColor: '#e8eef2' },
    reward: {
      unlockScore: 9000,
      area: '石巻',
      headline: '石巻魚市場に水揚げされた、鮮度の高い大型のマサバだけが「金華さば」を名乗れる。',
      tip: '漁法は定置網・一本釣り・まき網に限られ、ロット番号つきの認証シールで一尾ずつ追える。解禁日はその年の漁を見て決まる。',
      links: searchLinks('石巻市', '金華さば'),
    },
  },
  {
    id: 'fukahire',
    level: 9,
    name: '気仙沼のふかひれ',
    shortName: 'ふかひれ',
    image: '/assets/foods/miyagi/09_fukahire.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '姿煮にすると、繊維が一本ずつほどける。',
    source: 'https://www.kesennuma.miyagi.jp/sec/s081/1147933913730.html',
    icon: { label: '鱶鰭', shape: 'curry-plate', baseColor: '#d8b070', accentColor: '#5c3b1e' },
    reward: {
      unlockScore: 12000,
      area: '気仙沼',
      headline: '気仙沼市によると、気仙沼はヨシキリザメ・ネズミザメを中心にサメの水揚げが日本一。ふかひれの生産量も日本屈指。',
      tip: '同じ港は生鮮カツオの水揚げも連続日本一で、メカジキの水揚げ量も日本一。',
      links: searchLinks('気仙沼市', 'ふかひれ'),
    },
  },
  {
    id: 'gyutan-teishoku',
    level: 10,
    name: '牛たん焼き',
    shortName: '牛たん',
    image: '/assets/foods/miyagi/10_gyutan_teishoku.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '厚切りを炭火で。切り口は淡い桜色。',
    source: 'https://www.sentabi.jp/features/42',
    icon: { label: '牛たん', shape: 'curry-plate', baseColor: '#b5563f', accentColor: '#f0dfc0' },
    reward: {
      unlockScore: 16000,
      area: '仙台',
      headline: '戦後、仙台の店主・佐野啓四郎が試行錯誤のすえに考え出したとされる。',
      tip: '定食では麦飯とテールスープを添えるのが定番の形。外食の店と土産の物販、両方で伸びて仙台名物になった。',
      links: searchLinks('仙台市', '牛たん'),
    },
  },
  {
    id: 'seri-nabe',
    level: 11,
    name: '仙台せり鍋',
    shortName: 'せり鍋',
    image: '/assets/foods/miyagi/11_seri_nabe.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '根まで丸ごと。しゃきしゃきの音が主役。',
    source: 'https://shunsentanbou.pref.miyagi.jp/feature/detail_105.html',
    icon: { label: 'せり', shape: 'hotpot', baseColor: '#4e7d3f', accentColor: '#dce8c4' },
    reward: {
      unlockScore: 22000,
      area: '名取・仙台',
      headline: '宮城県はせりの生産量が全国1位。根・茎・葉を丸ごと食べるのが、せり鍋のいちばんの特徴。',
      tip: '名取のせり栽培は元和6年（1620年）ごろに自生の原種を改良して始まったとされ、およそ400年の歴史がある。根は30秒ほど煮るとしゃきしゃきになる。',
      links: searchLinks('仙台市', 'せり鍋'),
    },
  },
  {
    id: 'sendai-gyu',
    level: 12,
    name: '仙台牛のすき焼き',
    shortName: '仙台牛',
    image: '/assets/foods/miyagi/12_sendai_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '5等級だけが名乗れる、宮城の頂点。',
    source: 'https://www.pref.miyagi.jp/soshiki/tikusanka/sendaigyu.html',
    icon: { label: '仙台牛', shape: 'pot', baseColor: '#c14a4a', accentColor: '#f4e6d2' },
    reward: {
      unlockScore: 30000,
      area: '宮城県',
      headline: '「仙台牛」は宮城県内で肥育された黒毛和種のうち、肉質等級5等級に格付けされたものだけが名乗れる。',
      tip: '5等級に限っている銘柄牛はほかになく、5等級に届かないものは「仙台黒毛和牛」と呼び分けられる。',
      links: searchLinks('宮城県', '仙台牛 すき焼き'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const miyagiFoodStage: StageDefinition = {
  regionId: 'miyagi',
  regionName: '宮城県',
  mode: 'food',
  title: '宮城めしマージ',
  theme: {
    primaryColor: '#1f6f57',
    secondaryColor: '#d9a441',
    accentColor: '#f2d16b',
    backgroundColor: '#161b19',
  },
  /** 背景ツアー「宮城めぐり」。軸は季節。5月から翌4月へ一周する。 */
  scenery: {
    tour: miyagiSceneryTour,
  },
  // 画像の輪郭（自動生成）を付与する。
  items: attachHulls(FOOD_ITEMS, MIYAGI_FOOD_HULLS),
};
